import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

export default function Terms() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/apply" className="nav-link">Apply</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 max-w-4xl flex-1">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Terms of Service
        </h1>

        {/* Authority Site Reference */}
        <div className="bg-blue-50 border border-blue-200 p-4 mb-8">
          <p className="text-sm text-blue-800">
            These Terms of Service mirror the official terms published by the AI Accountability Standards Council.{' '}
            <a 
              href={`${AUTHORITY_SITE}/terms-of-service`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium inline-flex items-center gap-1"
            >
              View authoritative version <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>

        <div className="prose prose-zinc max-w-none">
          <p className="text-lg text-zinc-600">Effective Date: June 2025</p>

          <h2>1. Introduction</h2>
          <p>
            These Terms of Service ("Terms") govern access to and use of the AI Accountability Standards Council 
            ("AASC") website, standards documentation, declaration registry, and associated services.
          </p>
          <p>
            By accessing any AASC resource, you agree to these Terms. If you do not accept these Terms, 
            do not access any AASC resource.
          </p>

          <h2>2. Nature of Service</h2>
          <p>
            AASC publishes voluntary technical standards. AASC does not function as a regulatory authority, 
            verification body, or licensing agency.
          </p>
          <p>
            AASC declaration confirms that an applicant has submitted documentation demonstrating alignment 
            with defined control standards at the time of review.
          </p>
          <p>
            Accountability declaration does not imply endorsement, performance guarantees, regulatory compliance, or 
            transfer of liability.
          </p>

          <h2>3. Declaration Limitations</h2>
          <ul>
            <li>Declaration is conditional and revocable.</li>
            <li>Declaration does not guarantee performance.</li>
            <li>Declaration does not imply regulatory approval.</li>
            <li>Declaration does not transfer liability.</li>
            <li>Declaration reflects a point-in-time assessment and does not cover ongoing operations.</li>
            <li>AASC does not audit operational environments.</li>
          </ul>

          <h2>4. User Responsibilities</h2>
          <p>
            Users accessing AASC resources agree to:
          </p>
          <ul>
            <li>Use AASC materials only for lawful purposes.</li>
            <li>Not misrepresent declaration status.</li>
            <li>Not imply regulatory endorsement through AASC declaration.</li>
            <li>Comply with applicable laws in their jurisdiction.</li>
          </ul>

          <h2>5. Intellectual Property</h2>
          <p>
            AASC retains ownership of all standards documentation, declaration marks, and associated materials.
          </p>
          <p>
            Declared organizations may display AASC accountability marks subject to the Mark and Badge Usage 
            Policy published separately.
          </p>

          <h2>6. Disclaimer of Warranties</h2>
          <p>
            AASC provides all services "as is" without warranties of any kind, express or implied. 
            AASC does not warrant that materials are error-free, complete, or fit for any particular purpose.
          </p>

          <h2>7. Limitation of Liability</h2>
          <p>
            AASC is not liable for any direct, indirect, incidental, consequential, or punitive damages 
            arising from access to or use of AASC resources.
          </p>
          <p>
            AASC is not responsible for actions taken by declared or non-declared organizations. 
            Declaration does not create agency, partnership, or joint venture.
          </p>

          <h2>8. Modifications</h2>
          <p>
            AASC reserves the right to modify these Terms at any time. Material changes will be posted 
            on this page. Continued use after changes constitutes acceptance.
          </p>

          <h2>9. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the jurisdiction in which AASC is established. 
            Disputes will be resolved through binding arbitration.
          </p>

          <h2>10. Contact</h2>
          <p>
            Questions regarding these Terms should be directed to{' '}
            <a href="mailto:legal@aiaccountabilitycouncil.com" className="text-blue-600 hover:underline">
              legal@aiaccountabilitycouncil.com
            </a>.
          </p>
        </div>

        {/* Registry Platform Operational Terms */}
        <div className="mt-12 pt-8 border-t border-zinc-200">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Registry Platform Operational Terms
          </h2>
          <div className="prose prose-zinc max-w-none">
            <p className="text-zinc-600">
              The following terms apply specifically to the use of this Registry Platform in addition 
              to the AASC Terms of Service above.
            </p>

            <h3>Account Registration</h3>
            <p>
              Organizations applying for declaration must provide accurate information. 
              False statements may result in application denial or declaration withdrawal.
            </p>

            <h3>Payment Terms</h3>
            <p>
              Application fees are non-refundable once review has commenced. Refund requests 
              must be submitted within 7 days of payment. Renewals are billed annually.
            </p>

            <h3>Data Handling</h3>
            <p>
              Application data is stored securely and used only for declaration processing. 
              Registry listings are public. Refer to the Privacy Policy for full details.
            </p>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
